import { alpha, Button, Divider, Skeleton, Stack, Zoom } from "@mui/material";
import {
  COLOR_BLACK,
  COLOR_GOLD,
  COLOR_GREEN,
  COLOR_LIGHT_GRAY,
  COLOR_RED,
  COLOR_WHITE,
} from "../../../../../../data/colors/Colors";
import { CustomCardView } from "../../../../../component/CustomCardView";
import {
  DeleteOutlineRounded,
  EditOutlined,
  StarRateRounded,
} from "@mui/icons-material";
import { Difficulty, Question } from "../../../../../../domain/model/Question";
import useWindowDimensions from "../../../../../../core/utils/UseWindowDimensions";
import { CustomTextView } from "../../../../../component/CustomTextView";
import {
  formateDate,
  formatEnumLabel,
} from "../../../../../../core/utils/Utils";
import { STRINGS_NO_RATING_YET } from "../../../../../../data/strings/Strings";
import AutoHeight from "../../../../../component/AutoHeightView";
import { CustomImageView } from "../../../../../component/CustomImageView";

interface props {
  question: Question;
  index: number;
  href: string;
  cardBgColor?: string;
  onDeleteClicked: () => void;
}

export function QuestionsListItemView(props: props) {
  const { width } = useWindowDimensions();
  return (
    <CustomCardView
      padding={0}
      background={props.cardBgColor ?? COLOR_WHITE}
      borderRadius={6}
      children={
        <AutoHeight>
          <Stack direction={"row"} width={"100%"}>
            <Stack>
              <Stack spacing={1} alignItems={"center"} p={width < 1200 ? 1 : 2}>
                <Button
                  sx={{
                    width: "100%",
                    borderRadius: 44,
                    borderColor: COLOR_BLACK,
                    padding: 1,
                    "&:hover": {
                      borderColor: COLOR_LIGHT_GRAY,
                    },
                    "& .MuiTouchRipple-child": {
                      borderColor: COLOR_BLACK,
                    },
                  }}
                  variant="outlined"
                  LinkComponent={"a"}
                  target="_blank"
                  href={props.href}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
                    <EditOutlined
                      fontSize="small"
                      sx={{ color: COLOR_BLACK }}
                    />
                    <CustomTextView
                      text={"Edit"}
                      fontWeight="bold"
                      fontSize={14}
                      textColor={COLOR_BLACK}
                      props={{ textTransform: "none" }}
                    />
                  </Stack>
                </Button>
                <Button
                  sx={{
                    width: "100%",
                    borderRadius: 44,
                    borderColor: "red",
                    padding: 1,
                    "&:hover": {
                      borderColor: COLOR_LIGHT_GRAY,
                    },
                    "& .MuiTouchRipple-child": {
                      borderColor: "red",
                    },
                  }}
                  variant="outlined"
                  onClick={props.onDeleteClicked}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
                    <DeleteOutlineRounded
                      sx={{ color: "red" }}
                      fontSize="small"
                    />
                    <CustomTextView
                      text={"Delete"}
                      fontWeight="bold"
                      fontSize={14}
                      textColor={"red"}
                      props={{ textTransform: "none" }}
                    />
                  </Stack>
                </Button>
              </Stack>
              <Divider flexItem />
              {props.question.category?.title && (
                <Stack
                  position={"relative"}
                  minHeight={50}
                  justifyContent={"end"}
                >
                  <CustomImageView
                    src={props.question.category?.imageUrl}
                    width={"100%"}
                    height={"100%"}
                    props={{
                      bgcolor: COLOR_WHITE,
                      position: "absolute",
                    }}
                  />
                  <CustomTextView
                    text={`${props.question.category.title}`}
                    fontSize={13}
                    textAlign="center"
                    textColor={COLOR_WHITE}
                    fontWeight="bold"
                    props={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      overflowWrap: "break-word",
                      whiteSpace: "nowrap",
                      writingMode: "horizontal-tb",
                      direction: "rtl",
                      bgcolor: alpha(COLOR_BLACK, 0.6),
                      zIndex: 2,
                      p: 0.2,
                      position: "relative",
                      justifySelf: "end",
                    }}
                  />
                </Stack>
              )}
            </Stack>

            <Divider orientation="vertical" flexItem />
            <Stack
              direction={"row"}
              spacing={1}
              width={"100%"}
              alignItems={"center"}
              sx={{
                p: width < 1200 ? 1 : 2,
                overflow: "hidden",
              }}
            >
              <Stack
                width={"100%"}
                height={"100%"}
                justifyContent={"space-between"}
              >
                <Stack>
                  <Zoom in timeout={800}>
                    <div>
                      <CustomTextView
                        text={`${props.question.title
                          ?.replace("?", "")
                          .replace("؟", "")}؟`}
                        fontWeight="500"
                        fontSize={26}
                        props={{
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          overflowWrap: "break-word",
                          whiteSpace: "nowrap",
                          writingMode: "horizontal-tb",
                          direction: "rtl",
                        }}
                      />
                    </div>
                  </Zoom>
                  {props.question.answerTitle != undefined ? (
                    <Zoom in timeout={400} style={{ minHeight: 40 }}>
                      <div>
                        <CustomTextView
                          text={props.question.answerTitle}
                          fontSize={20}
                          textColor={COLOR_BLACK}
                          fontWeight="300"
                          props={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            overflowWrap: "break-word",
                            whiteSpace: "nowrap",
                            writingMode: "horizontal-tb",
                            direction: "rtl",
                            height: 40,
                          }}
                        />
                      </div>
                    </Zoom>
                  ) : (
                    <Stack sx={{ direction: "rtl" }}>
                      <Skeleton
                        width={100}
                        height={40}
                        sx={{ borderRadius: 2 }}
                      />
                    </Stack>
                  )}
                </Stack>

                <CustomTextView
                  text={formatEnumLabel(props.question.difficulty ?? "")}
                  textColor={
                    props.question.difficulty == Difficulty.EASY
                      ? COLOR_GREEN
                      : props.question.difficulty == Difficulty.MEDIUM
                        ? COLOR_GOLD
                        : COLOR_RED
                  }
                  fontWeight="bold"
                  fontSize={16}
                />

                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <CustomTextView
                    textColor={COLOR_LIGHT_GRAY}
                    fontSize={14}
                    text={formateDate(
                      props.question.createdAt ?? new Date(),
                      "DD MMM - hh:mm A",
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
            <CustomTextView
              text={`#${props.index + 1}`}
              variant="h6"
              textColor={COLOR_LIGHT_GRAY}
              fontWeight="300"
              props={{
                pt: width < 1200 ? 1 : 2,
                pb: width < 1200 ? 1 : 2,
                pr: width < 1200 ? 1 : 2,
              }}
            />
          </Stack>
        </AutoHeight>
      }
    />
  );
}
